import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export const DOCUMENT_TYPES = ['identity', 'licence'];
export const VALIDATION_TABS = ['pending', 'validated'];

const readParam = (searchParams, name, allowed) => {
  const value = searchParams.get(name);
  return allowed.includes(value) ? value : allowed[0];
};

/**
 * The document type and the tab of the identity validation screen live in the
 * URL (?type=identity|licence&tab=pending|validated), so a link or a reload
 * lands on the same list. A missing or unknown value reads as the default.
 * Writes replace the history entry, as switching lists is not a navigation.
 */
const useIdentityValidationParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const type = readParam(searchParams, 'type', DOCUMENT_TYPES);
  const tab = readParam(searchParams, 'tab', VALIDATION_TABS);

  const writeParams = useCallback(
    (values) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(values).forEach(([name, value]) =>
            next.set(name, value),
          );
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Both params are written each time, so the URL always says the list shown.
  const setType = useCallback(
    (nextType) => {
      if (!DOCUMENT_TYPES.includes(nextType)) return;
      writeParams({ type: nextType, tab });
    },
    [writeParams, tab],
  );

  const setTab = useCallback(
    (nextTab) => {
      if (!VALIDATION_TABS.includes(nextTab)) return;
      writeParams({ type, tab: nextTab });
    },
    [writeParams, type],
  );

  return { type, tab, setType, setTab };
};

export default useIdentityValidationParams;
